import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Radio,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";

import { ReactComponent as CheckIcon } from "assets/images/icons/btn_edit_done.svg";
import { ReactComponent as CopyIcon } from "assets/images/icons/btn_copy.svg";
import { ReactComponent as LockIcon } from "assets/images/icons/icn_table_cell_Lock.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";

const steps = [
  {
    number: 1,
    label: "Configure",
  },
  {
    number: 2,
    label: "Confirm",
  },
  {
    number: 3,
    label: "Token issued",
  },
];

const accessLevelOptions = [
  {
    value: "read-only",
    title: "Read-only",
    description:
      "Support can view everything in scope but change nothing (Viewer-equivalent).",
  },
  {
    value: "full-control",
    title: "Full-control",
    description:
      "Support can change configuration to troubleshoot (Admin-equivalent). Billing/License and Users/RBAC stay view-only; the Support section is off-limits.",
  },
];

const SupportToken = ({ open, onClose, scopeType, onNext, onDone }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [accessLevel, setAccessLevel] = useState("read-only");
  const [expiresAfter, setExpiresAfter] = useState("30");
  const [customExpiryDate, setCustomExpiryDate] = useState("");
  const [reason, setReason] = useState("");

  const [formData, setFormData] = useState(null);
  const [generatedSession, setGeneratedSession] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setCurrentStep(1);
    setAccessLevel("read-only");
    setExpiresAfter("30");
    setCustomExpiryDate("");
    setReason("");
    setFormData(null);
    setGeneratedSession(null);
    setCopied(false);
    setIsGenerating(false);
    setOpenErrorModal(false);
    setErrorMessage("");
  }, [open]);

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const normalizeTimestamp = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "number") {
      return value < 100000000000 ? value * 1000 : value;
    }

    const stringValue = String(value).trim();

    if (!stringValue) {
      return null;
    }

    if (/^\d+$/.test(stringValue)) {
      const numberValue = Number(stringValue);

      return numberValue < 100000000000 ? numberValue * 1000 : numberValue;
    }

    const parsedValue = Date.parse(stringValue);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  };

  const formatDisplayDate = (timestamp) => {
    const normalizedTimestamp = normalizeTimestamp(timestamp);

    if (!normalizedTimestamp) {
      return "-";
    }

    return new Date(normalizedTimestamp).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const today = new Date();

  const maxExpiryDate = new Date();
  maxExpiryDate.setDate(maxExpiryDate.getDate() + 90);

  const minExpiryDateString = formatDateForInput(today);
  const maxExpiryDateString = formatDateForInput(maxExpiryDate);

  const generatedToken =
    generatedSession?.token ?? generatedSession?.supportToken ?? "";

  const getExpireAt = () => {
    if (expiresAfter === "custom") {
      if (!customExpiryDate) {
        return null;
      }

      const customDate = new Date(`${customExpiryDate}T23:59:59.999`);

      return customDate.getTime();
    }

    const expireDate = new Date();

    expireDate.setDate(expireDate.getDate() + Number(expiresAfter));

    return expireDate.getTime();
  };

  const getAccessLevelLabel = () => {
    return accessLevel === "read-only" ? "Read-only" : "Full-control";
  };

  const getExpiryDescription = () => {
    if (!formData?.expireAt) {
      return "-";
    }

    if (expiresAfter === "custom") {
      return formatDisplayDate(formData.expireAt);
    }

    return `${formatDisplayDate(formData.expireAt)} (in ${expiresAfter} days)`;
  };

  const handleNext = () => {
    const expireAt = getExpireAt();

    if (!expireAt) {
      return;
    }

    const nextFormData = {
      scopeType,
      accessLevel: accessLevel === "read-only" ? "read_only" : "full_control",
      expireAt,
      reason: reason.trim(),
    };

    setFormData(nextFormData);
    setCurrentStep(2);
  };

  const handleBack = () => {
    if (isGenerating) {
      return;
    }

    setCurrentStep(1);
  };

  const handleGenerateToken = async () => {
    if (!formData || isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      setErrorMessage("");
      setOpenErrorModal(false);

      const response = await onNext?.(formData);

      const responseError =
        response?.error ??
        response?.data?.error ??
        response?.result?.error ??
        response?.data?.result?.error ??
        null;

      if (responseError) {
        setErrorMessage(
          responseError?.message || "Failed to generate support token.",
        );

        setOpenErrorModal(true);
        return;
      }

      const responseData =
        response?.result?.data ??
        response?.data?.result?.data ??
        response?.data?.data ??
        response?.data ??
        response ??
        null;

      setGeneratedSession({
        ...responseData,

        scopeType: responseData?.scopeType ?? formData.scopeType,
        accessLevel: responseData?.accessLevel ?? formData.accessLevel,
        expireAt: responseData?.expireAt ?? formData.expireAt,
        reason: responseData?.reason ?? formData.reason,
      });

      setCopied(false);
      setCurrentStep(3);
    } catch (error) {
      console.error("Failed to generate support token:", error);

      const message =
        error?.error?.message ??
        error?.response?.data?.error?.message ??
        error?.response?.data?.message ??
        error?.message ??
        "Failed to generate support token. Please try again.";

      setErrorMessage(message);
      setOpenErrorModal(true);
    } finally {
      setIsGenerating(false);
    }
  };
  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessage("");
  };

  const getSelectedScopeLabel = () => {
    try {
      const selectBreadcrumbs = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );

      const realm = selectBreadcrumbs?.realm || {};
      const orgs = selectBreadcrumbs?.orgs || {};
      const site = selectBreadcrumbs?.site || {};

      if (site?.id || Object.keys(site).length > 0) {
        return `Site: ${site?.name || "-"}`;
      }
      if (orgs?.id || Object.keys(orgs).length > 0) {
        return `Org: ${orgs?.name || "-"}`;
      }
      if (realm?.id && realm?.hasManageOrg === false) {
        return `Org: ${realm?.name || "-"}`;
      }

      return "-";
    } catch (error) {
      console.error("Failed to parse selectBreadcrumbs:", error);

      return "-";
    }
  };

  const handleCopyToken = async () => {
    if (!generatedToken) {
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedToken);

      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      console.error("Failed to copy token:", error);
    }
  };

  const handleDone = () => {
    if (!generatedSession || !generatedToken) {
      return;
    }

    onDone?.(generatedSession);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="generate-support-token-title"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(31, 42, 68, 0.42)",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",

            width: "720px",
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",

            display: "flex",
            flexDirection: "column",

            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 18px 50px rgba(25, 35, 70, 0.25)",
            outline: "none",
          }}
        >
          {/* Modal header */}
          <Box
            sx={{
              flexShrink: 0,
              minHeight: "60px",
              px: "18px",
              py: "14px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E1E5EE",
            }}
          >
            <Typography
              id="generate-support-token-title"
              component="div"
              sx={{
                color: "#1E3476",
                fontSize: "20px",
                fontWeight: 700,
              }}
            >
              Generate Support Token
            </Typography>

            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal content */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              px: "18px",
              py: "18px",
              overflowY: "auto",
              boxSizing: "border-box",
            }}
          >
            {/* Steps */}
            <Box
              sx={{
                width: "100%",
                mb: currentStep === 3 ? "24px" : "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {steps.map((step, index) => {
                const isCompleted = step.number < currentStep;
                const isActive = step.number === currentStep;

                return (
                  <React.Fragment key={step.number}>
                    <Box
                      sx={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Box
                        sx={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color:
                            isCompleted || isActive ? "#FFFFFF" : "#8E98AB",
                          backgroundColor: isCompleted
                            ? "#07952B"
                            : isActive
                              ? "#20377C"
                              : "#E8EBF2",
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                      >
                        {isCompleted ? (
                          <CheckIcon
                            sx={{
                              fontSize: "18px",
                            }}
                          />
                        ) : (
                          step.number
                        )}
                      </Box>

                      <Typography
                        sx={{
                          color: isActive
                            ? "#20377C"
                            : isCompleted
                              ? "#687184"
                              : "#7E8799",
                          fontSize: "14px",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>

                    {index < steps.length - 1 && (
                      <Box
                        sx={{
                          flex: 1,
                          height: "2px",
                          mx: "12px",
                          backgroundColor:
                            step.number < currentStep ? "#07952B" : "#DCE1EB",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>

            {/* Step 1: Configure */}
            {currentStep === 1 && (
              <>
                {/* Access scope */}
                <Box sx={{ mb: "16px" }}>
                  <Typography
                    sx={{
                      mb: "5px",
                      color: "#20377C",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    Access scope
                  </Typography>

                  <TextField
                    fullWidth
                    disabled
                    value={getSelectedScopeLabel()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "42px",
                        borderRadius: "7px",
                        backgroundColor: "#F5F6FA",

                        "& fieldset": {
                          borderColor: "#CBD2E1",
                        },

                        "&.Mui-disabled fieldset": {
                          borderColor: "#CBD2E1",
                        },
                      },

                      "& .MuiInputBase-input": {
                        px: "14px",
                        py: 0,
                        fontSize: "14px",
                      },

                      "& .MuiInputBase-input.Mui-disabled": {
                        color: "#747D8F",
                        WebkitTextFillColor: "#747D8F",
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      mt: "5px",
                      color: "#70798C",
                      fontSize: "12px",
                      lineHeight: 1.5,
                    }}
                  >
                    Set by where you opened this — switch pages to change scope.
                  </Typography>
                </Box>

                {/* Access level */}
                <Box sx={{ mb: "20px" }}>
                  <Typography
                    sx={{
                      mb: "6px",
                      color: "#20377C",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    Access level *
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    {accessLevelOptions.map((option) => {
                      const selected = accessLevel === option.value;

                      return (
                        <Box
                          key={option.value}
                          role="radio"
                          aria-checked={selected}
                          tabIndex={0}
                          onClick={() => setAccessLevel(option.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();

                              setAccessLevel(option.value);
                            }
                          }}
                          sx={{
                            px: "12px",
                            py: "10px",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "flex-start",
                            border: selected
                              ? "1px solid #315DFF"
                              : "1px solid #CBD2E1",
                            borderRadius: "7px",
                            backgroundColor: selected ? "#F0F3FF" : "#FFFFFF",
                            cursor: "pointer",
                            transition: "0.2s",

                            "&:hover": {
                              borderColor: "#315DFF",
                            },
                          }}
                        >
                          <Radio
                            checked={selected}
                            value={option.value}
                            size="small"
                            onChange={() => setAccessLevel(option.value)}
                            sx={{
                              flexShrink: 0,
                              p: "2px",
                              mt: "1px",
                              mr: "10px",
                              color: "#9AA2B1",

                              "&.Mui-checked": {
                                color: "#176BFF",
                              },

                              "& .MuiSvgIcon-root": {
                                fontSize: "18px",
                              },
                            }}
                          />

                          <Typography
                            component="div"
                            sx={{
                              pt: "1px",
                              color: "#687184",
                              fontSize: "13px",
                              fontWeight: 600,
                              lineHeight: 1.35,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                mr: "4px",
                                color: "#20377C",
                                fontWeight: 700,
                              }}
                            >
                              {option.title}
                            </Box>

                            {option.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Expires after */}
                <Box sx={{ mb: "20px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "flex-start",

                      "@media (max-width: 600px)": {
                        flexDirection: "column",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "calc(50% - 8px)",
                        minWidth: 0,

                        "@media (max-width: 600px)": {
                          width: "100%",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          mb: "6px",
                          color: "#20377C",
                          fontSize: "14px",
                          fontWeight: 700,
                        }}
                      >
                        Expires after *
                      </Typography>

                      <FormControl fullWidth>
                        <Select
                          value={expiresAfter}
                          onChange={(event) => {
                            const value = event.target.value;

                            setExpiresAfter(value);

                            if (value !== "custom") {
                              setCustomExpiryDate("");
                            }
                          }}
                          MenuProps={{
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                            PaperProps: {
                              sx: {
                                mt: "4px",
                                maxHeight: "240px",
                              },
                            },
                          }}
                          sx={{
                            height: "42px",
                            borderRadius: "7px",
                            color: "#4F5665",
                            fontSize: "14px",

                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              py: 0,
                            },

                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#CBD2E1",
                            },

                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#AAB4C7",
                            },

                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderWidth: "1px",
                              borderColor: "#315DFF",
                            },
                          }}
                        >
                          <MenuItem value="7">7 days</MenuItem>

                          <MenuItem value="14">14 days</MenuItem>

                          <MenuItem value="30">30 days</MenuItem>

                          <MenuItem value="60">60 days</MenuItem>

                          <MenuItem value="90">90 days</MenuItem>

                          <MenuItem value="custom">Custom</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Box
                      sx={{
                        width: "calc(50% - 8px)",
                        minWidth: 0,
                      }}
                    >
                      {expiresAfter === "custom" && (
                        <>
                          <Typography
                            sx={{
                              mb: "6px",
                              color: "#20377C",
                              fontSize: "14px",
                              fontWeight: 700,
                            }}
                          >
                            Custom expiry date *
                          </Typography>

                          <TextField
                            fullWidth
                            type="date"
                            value={customExpiryDate}
                            onChange={(event) => {
                              setCustomExpiryDate(event.target.value);
                            }}
                            inputProps={{
                              min: minExpiryDateString,
                              max: maxExpiryDateString,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                height: "42px",
                                borderRadius: "7px",

                                "& fieldset": {
                                  borderColor: "#CBD2E1",
                                },

                                "&:hover fieldset": {
                                  borderColor: "#AAB4C7",
                                },

                                "&.Mui-focused fieldset": {
                                  borderWidth: "1px",
                                  borderColor: "#315DFF",
                                },
                              },

                              "& .MuiInputBase-input": {
                                fontSize: "14px",
                                color: "#4F5665",
                              },
                            }}
                          />

                          <Typography
                            sx={{
                              mt: "5px",
                              color: "#70798C",
                              fontSize: "12px",
                              lineHeight: 1.5,
                            }}
                          >
                            At most 90 days from today.
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Reason */}
                <Box>
                  <Typography
                    sx={{
                      mb: "6px",
                      color: "#20377C",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    Reason / note
                  </Typography>

                  <TextField
                    fullWidth
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Optional — what should support look at?"
                    inputProps={{
                      maxLength: 500,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "42px",
                        borderRadius: "7px",

                        "& fieldset": {
                          borderColor: "#CBD2E1",
                        },

                        "&:hover fieldset": {
                          borderColor: "#AAB4C7",
                        },

                        "&.Mui-focused fieldset": {
                          borderWidth: "1px",
                          borderColor: "#315DFF",
                        },
                      },

                      "& .MuiInputBase-input": {
                        fontSize: "14px",
                      },

                      "& input::placeholder": {
                        color: "#818898",
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {/* Step 2: Confirm */}
            {currentStep === 2 && (
              <Box>
                <Typography
                  sx={{
                    mb: "12px",
                    color: "#70798C",
                    fontSize: "14px",
                    lineHeight: 1.5,
                  }}
                >
                  Review what you're about to grant. No token exists yet.
                </Typography>

                <Box
                  sx={{
                    p: "20px",
                    border: "1px solid #DCE2FF",
                    borderRadius: "8px",
                    backgroundColor: "#F2F4FF",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "120px 1fr",
                        sm: "170px 1fr",
                      },
                      columnGap: "8px",
                      rowGap: "5px",
                      mb: "16px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Scope
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {getSelectedScopeLabel()}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Access level
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {getAccessLevelLabel()}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Expires
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {getExpiryDescription()}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Reason
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      {reason.trim() || "—"}
                    </Typography>
                  </Box>

                  {accessLevel === "full-control" ? (
                    <Box>
                      <Typography
                        sx={{
                          mb: "8px",
                          color: "#20377C",
                          fontSize: "14px",
                          fontWeight: 700,
                          lineHeight: 1.3,
                        }}
                      >
                        With Full-control, support can change settings to
                        troubleshoot. These two stay view-only — visible, never
                        editable:
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          mb: "6px",
                        }}
                      >
                        <LockIcon />

                        <Typography
                          sx={{
                            color: "#4F5665",
                            fontSize: "14px",
                          }}
                        >
                          Billing / License Management —{" "}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 700,
                            }}
                          >
                            view-only
                          </Box>
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <LockIcon />

                        <Typography
                          sx={{
                            color: "#4F5665",
                            fontSize: "14px",
                          }}
                        >
                          Users / RBAC —{" "}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 700,
                            }}
                          >
                            view-only
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                        lineHeight: 1.4,
                      }}
                    >
                      With Read-only access, support can view information in
                      this scope but cannot change any settings.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Step 3: Token issued */}
            {currentStep === 3 && (
              <Box
                sx={{
                  width: "100%",
                  pb: "20px",
                }}
              >
                <Box
                  sx={{
                    mt: "36px",
                    mb: "14px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box>
                    <CheckIcon width="64px" height="64px" />
                  </Box>
                </Box>

                <Typography
                  sx={{
                    color: "#172B68",
                    fontSize: "20px",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Support token generated
                </Typography>

                <Typography
                  sx={{
                    mt: "12px",
                    px: {
                      xs: 0,
                      sm: "20px",
                    },
                    color: "#70798C",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    textAlign: "center",
                  }}
                >
                  Send this token to your D-Link support contact (e.g. by
                  email). They redeem it with their own support account.
                </Typography>

                {/* Token */}
                <Box
                  sx={{
                    mt: "16px",
                    mx: "auto",
                    width: "520px",
                    maxWidth: "100%",
                    minHeight: "54px",
                    px: "14px",
                    py: "10px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    border: "1px solid #DCE2FF",
                    borderRadius: "7px",
                    backgroundColor: "#F2F4FF",
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: "#20377C",
                      fontSize: "14px",
                      fontFamily: "monospace",
                      letterSpacing: "0.4px",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {generatedToken || "-"}
                  </Typography>

                  <OutlineRectangleButton
                    width="100px"
                    height="32px"
                    startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                    disabled={!generatedToken}
                    onClick={handleCopyToken}
                    sx={{
                      "& .MuiButton-startIcon svg": {
                        width: "20px",
                        height: "20px",
                      },
                    }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </OutlineRectangleButton>
                </Box>

                {/* Token information */}
                <Box
                  sx={{
                    mt: "18px",
                    mx: "auto",
                    width: "520px",
                    maxWidth: "100%",
                    p: "22px 20px",
                    boxSizing: "border-box",
                    border: "1px solid #DCE2FF",
                    borderRadius: "7px",
                    backgroundColor: "#F2F4FF",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "120px 1fr",
                        sm: "170px 1fr",
                      },
                      columnGap: "8px",
                      rowGap: "6px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Scope
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {getSelectedScopeLabel()}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Access level
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {getAccessLevelLabel()}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Status
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      Awaiting access
                    </Typography>

                    <Typography
                      sx={{
                        color: "#70798C",
                        fontSize: "14px",
                      }}
                    >
                      Expires
                    </Typography>

                    <Typography
                      sx={{
                        color: "#20377C",
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {formData?.expireAt
                        ? formatDisplayDate(formData.expireAt)
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              flexShrink: 0,
              minHeight: "72px",
              px: "18px",
              py: "14px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid #E1E5EE",
              backgroundColor: "#FAFBFD",
            }}
          >
            {currentStep === 1 && (
              <>
                <OutlineButton width="80px" height="32px" onClick={onClose}>
                  Cancel
                </OutlineButton>

                <SolidButton
                  disabled={expiresAfter === "custom" && !customExpiryDate}
                  width="80px"
                  height="32px"
                  onClick={handleNext}
                >
                  Next
                </SolidButton>
              </>
            )}

            {currentStep === 2 && (
              <>
                <OutlineButton
                  width="80px"
                  height="32px"
                  onClick={handleBack}
                  disabled={isGenerating}
                >
                  Back
                </OutlineButton>
                <SolidButton
                  onClick={handleGenerateToken}
                  disabled={isGenerating}
                  width="140px"
                  height="32px"
                >
                  {isGenerating ? "Generating..." : "Generate Token"}
                </SolidButton>
              </>
            )}

            {currentStep === 3 && (
              <>
                <SolidButton onClick={handleDone} width="80px" height="32px">
                  Done
                </SolidButton>
              </>
            )}
          </Box>
        </Box>
      </Modal>
      <Modal
        open={openErrorModal}
        onClose={handleCloseErrorModal}
        aria-labelledby="generate-token-error-title"
        aria-describedby="generate-token-error-description"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(31, 42, 68, 0.42)",
            },
          },
        }}
        sx={{
          zIndex: 1400,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "500px",
            maxWidth: "calc(100vw - 32px)",
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            overflow: "hidden",
            outline: "none",
            boxShadow: "0 18px 50px rgba(25, 35, 70, 0.25)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              minHeight: "60px",
              px: "18px",
              py: "14px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E1E5EE",
            }}
          >
            <Typography
              id="generate-token-error-title"
              sx={{
                color: "#B42318",
                fontSize: "20px",
                fontWeight: 700,
              }}
            >
              Unable to generate token
            </Typography>

            <IconButton
              onClick={handleCloseErrorModal}
              aria-label="Close error modal"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              px: "18px",
              py: "24px",
            }}
          >
            <Typography
              id="generate-token-error-description"
              sx={{
                color: "#667085",
                fontSize: "14px",
                lineHeight: 1.6,
                wordBreak: "break-word",
              }}
            >
              {errorMessage}
            </Typography>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              minHeight: "72px",
              px: "18px",
              py: "14px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              borderTop: "1px solid #E1E5EE",
              backgroundColor: "#FAFBFD",
            }}
          >
            <Button
              variant="contained"
              onClick={handleCloseErrorModal}
              sx={{
                minWidth: "84px",
                height: "38px",
                px: "20px",
                borderRadius: "20px",
                backgroundColor: "#20377C",
                color: "#FFFFFF",
                textTransform: "none",
                boxShadow: "none",
                fontSize: "14px",
                fontWeight: 700,

                "&:hover": {
                  backgroundColor: "#172B68",
                  boxShadow: "none",
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SupportToken;
